/**
 * Optimized Predictive Search Web Component
 * Fetches server-side rendered HTML from /search/suggest endpoint
 */

class OptimizedPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cache = new Map();
    this.debounceTimer = null;
    this.debounceDelay = 300;
    this.selectedIndex = -1;
    this.allResults = [];
    this.searchInput = null;
    this.resultsContainer = null;
    this.bestSellersContainer = null;
    this.isOverlayVisible = false;
    this.overlayEl = null;
  }

  connectedCallback() {
    this.initialize();
  }

  initialize() {
    this.searchInput = this.querySelector('input[type="search"]');
    if (!this.searchInput) return;

    this.clearButton = this.querySelector('.header-search-bar__clear');
    this.bestSellersContainer = this.querySelector('.optimized-predictive-search__best-sellers');
    this.setupResultsContainer();
    this.ensureOverlayElement();
    this.setupDropdownObservers();
    this.attachEventListeners();
    this.updateClearButton();

    this.form = this.querySelector('.header-search-bar__form');
    if (this.form && this.searchInput) {
      this.form.addEventListener('submit', (e) => {
        if (this.searchInput.value.trim() === '') {
          e.preventDefault(); // Prevent navigating to the search page for empty searches
          this.showBestSellers(); // Optional: focus on showing best sellers if not already shown
        }
      });
    }
  }

  ensureOverlayElement() {
    // Reuse a single overlay element for the whole page
    const existing = document.querySelector('.optimized-predictive-search__overlay');
    if (existing) {
      this.overlayEl = existing;
      // Enhance: clicking overlay also closes the mobile drawer if open
      this.overlayEl.addEventListener('click', () => {
        const drawer = document.querySelector('mobile-menu-drawer.is-open');
        if (drawer && typeof drawer.closeDrawer === 'function') {
          drawer.closeDrawer();
        }
      });
      return;
    }
    const overlay = document.createElement('div');
    overlay.className = 'optimized-predictive-search__overlay';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.addEventListener('click', () => {
      this.hideResults();
      this.hideBestSellers();
      const drawer = document.querySelector('mobile-menu-drawer.is-open');
      if (drawer && typeof drawer.closeDrawer === 'function') {
        drawer.closeDrawer();
      }
    });
    document.body.appendChild(overlay);
    this.overlayEl = overlay;
  }

  showOverlay() {
    if (!this.overlayEl) return;
    const body = document.body;
    const current = parseInt(body.dataset.overlayVisibleCount || '0', 10);
    body.dataset.overlayVisibleCount = String(current + 1);
    this.overlayEl.classList.add('is-visible');
  }

  hideOverlay() {
    if (!this.overlayEl) return;
    const body = document.body;
    const current = parseInt(body.dataset.overlayVisibleCount || '0', 10);
    const next = Math.max(0, current - 1);
    body.dataset.overlayVisibleCount = String(next);
    if (next === 0) {
      this.overlayEl.classList.remove('is-visible');
    }
  }

  setupResultsContainer() {
    this.resultsContainer = document.createElement('div');
    this.resultsContainer.className = 'optimized-predictive-search__dropdown';
    this.resultsContainer.setAttribute('role', 'presentation');
    this.searchInput.parentElement.insertAdjacentElement('afterend', this.resultsContainer);
  }

  attachEventListeners() {
    this.searchInput.addEventListener('input', () => this.handleInput());
    this.searchInput.addEventListener('keydown', (e) => this.handleKeydown(e));
    this.searchInput.addEventListener('focus', () => this.handleFocus());
    document.addEventListener('click', (e) => this.handleClickOutside(e));
    
    // Handle sticky header position changes
    window.addEventListener('scroll', () => this.handleScroll());
    window.addEventListener('resize', () => this.handleResize());
    
    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => this.handleClear());
    }
  }

  lockBodyScroll() {
    // Use a reference counter on body to avoid unlocking when other overlays are open
    const body = document.body;
    const isMobile = window.matchMedia('(max-width: 1023px)').matches;
    if (!isMobile) return;
    const current = parseInt(body.dataset.scrollLockCount || '0', 10);
    body.dataset.scrollLockCount = String(current + 1);
    body.style.overflow = 'hidden';
  }

  unlockBodyScroll() {
    const body = document.body;
    const isMobile = window.matchMedia('(max-width: 1023px)').matches;
    if (!isMobile) return;
    const current = parseInt(body.dataset.scrollLockCount || '0', 10);
    const next = Math.max(0, current - 1);
    body.dataset.scrollLockCount = String(next);
    if (next === 0) {
      body.style.overflow = '';
    }
  }

  handleFocus() {
    const query = this.searchInput.value.trim();
    if (query.length === 0) {
      this.showBestSellers();
    } else if (query.length >= 2) {
      if (this.cache.has(query)) {
        this.renderResults(this.cache.get(query));
      } else if (this.resultsContainer.innerHTML.trim()) {
        this.showResults();
      } else {
        this.fetchResults(query);
      }
    }
  }

  handleInput() {
    clearTimeout(this.debounceTimer);
    const query = this.searchInput.value.trim();
    
    this.updateClearButton();

    if (query.length === 0) {
      this.showBestSellers();
      return;
    }

    // For small queries(less than 2 characters), hide everything
    if (query.length < 2) {
      this.hideResults();
      this.hideBestSellers();
      return;
    }

    this.hideBestSellers();
    this.debounceTimer = setTimeout(() => {
      this.fetchResults(query);
    }, this.debounceDelay);
  }

  handleClear() {
    this.searchInput.value = '';
    this.updateClearButton();
    this.hideResults();
    this.showBestSellers();
    this.searchInput.focus();
  }

  updateClearButton() {
    if (!this.clearButton) return;
    
    if (this.searchInput.value.trim().length > 0) {
      this.clearButton.style.display = 'block';
    } else {
      this.clearButton.style.display = 'none';
    }
  }

  showBestSellers() {
    if (!this.bestSellersContainer) return;
    
    this.hideResults();
    
    this.bestSellersContainer.classList.add('optimized-predictive-search__dropdown--visible');
    this.updateMobilePosition();
    if (!this.isOverlayVisible) {
      this.lockBodyScroll();
      this.isOverlayVisible = true;
    }
    this.updateOverlayVisibilityBasedOnDropdown();
    
    this.allResults = [];
    const resultLinks = this.bestSellersContainer.querySelectorAll('a[role="option"]');
    resultLinks.forEach((link) => {
      this.allResults.push(link);
    });
    this.selectedIndex = -1;
  }

  hideBestSellers() {
    if (this.bestSellersContainer) {
      this.bestSellersContainer.classList.remove('optimized-predictive-search__dropdown--visible');
    }
    // If neither results nor best sellers are visible, unlock body
    const resultsVisible = this.resultsContainer && this.resultsContainer.classList.contains('optimized-predictive-search__dropdown--visible');
    const bestSellersVisible = this.bestSellersContainer && this.bestSellersContainer.classList.contains('optimized-predictive-search__dropdown--visible');
    if (!resultsVisible && !bestSellersVisible && this.isOverlayVisible) {
      this.unlockBodyScroll();
      this.isOverlayVisible = false;
      this.updateOverlayVisibilityBasedOnDropdown();
    }
  }

  async fetchResults(query) {
    // Check cache first
    if (this.cache.has(query)) {
      this.renderResults(this.cache.get(query));
      return;
    }

    try {
      const resourceFields = [
        'title',
        'product_type',
        'variants.title',
        'vendor'
      ].join(',');
      
      const url = `${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(query)}&resources[type]=product,collection,article,page,query&resources[options][unavailable_products]=last&resources[options][fields]=${encodeURIComponent(resourceFields)}&section_id=optimized-predictive-search`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'text/html',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      
      // Cache the rendered HTML
      this.cache.set(query, html);
      
      this.renderResults(html);
    } catch (error) {
      this.hideResults();
    }
  }

  renderResults(html) {
    // Parse the HTML response to extract content
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const content = doc.querySelector('.optimized-predictive-search__content');

    if (!content || !content.innerHTML.trim()) {
      this.hideResults();
      return;
    }

    // Inject the rendered HTML
    this.resultsContainer.innerHTML = content.outerHTML;
    this.setupResultTracking();
    this.showResults();
    this.selectedIndex = -1;
  }

  setupResultTracking() {
    // Build a list of all focusable results for keyboard navigation
    this.allResults = [];
    const resultLinks = this.resultsContainer.querySelectorAll('a[role="option"]');
    resultLinks.forEach((link) => {
      this.allResults.push(link);
    });
  }

  handleKeydown(e) {
    // Handle Escape key regardless of dropdown visibility
    if (e.key === 'Escape') {
      e.preventDefault();
      this.hideResults();
      this.hideBestSellers();
      this.searchInput.focus();
      return;
    }

    if (!this.resultsContainer.classList.contains('optimized-predictive-search__dropdown--visible')) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.allResults.length - 1);
        this.updateSelection();
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
        this.updateSelection();
        break;
      case 'Enter':
        if (this.selectedIndex >= 0 && this.allResults[this.selectedIndex]) {
          e.preventDefault();
          this.allResults[this.selectedIndex].click();
        }
        break;
      case 'Escape':
        e.preventDefault();
        this.hideResults();
        this.searchInput.focus();
        break;
    }
  }

  updateSelection() {
    this.allResults.forEach((link, index) => {
      if (index === this.selectedIndex) {
        link.classList.add('optimized-predictive-search__item--selected');
        link.setAttribute('aria-selected', 'true');
        link.scrollIntoView({ block: 'nearest' });
      } else {
        link.classList.remove('optimized-predictive-search__item--selected');
        link.setAttribute('aria-selected', 'false');
      }
    });
  }

  showResults() {
    if (this.resultsContainer.innerHTML.trim()) {
      this.resultsContainer.classList.add('optimized-predictive-search__dropdown--visible');
      this.updateMobilePosition();
      if (!this.isOverlayVisible) {
        this.lockBodyScroll();
        this.isOverlayVisible = true;
      }
      this.updateOverlayVisibilityBasedOnDropdown();
    }
  }

  hideResults() {
    this.resultsContainer.classList.remove('optimized-predictive-search__dropdown--visible');
    this.selectedIndex = -1;
    // If neither results nor best sellers are visible, unlock body
    const resultsVisible = this.resultsContainer && this.resultsContainer.classList.contains('optimized-predictive-search__dropdown--visible');
    const bestSellersVisible = this.bestSellersContainer && this.bestSellersContainer.classList.contains('optimized-predictive-search__dropdown--visible');
    if (!resultsVisible && !bestSellersVisible && this.isOverlayVisible) {
      this.unlockBodyScroll();
      this.isOverlayVisible = false;
      this.updateOverlayVisibilityBasedOnDropdown();
    }
  }

  setupDropdownObservers() {
    const update = () => this.updateOverlayVisibilityBasedOnDropdown();
    const observer = new MutationObserver(update);
    if (this.resultsContainer) {
      observer.observe(this.resultsContainer, { attributes: true, attributeFilter: ['class'] });
    }
    if (this.bestSellersContainer) {
      observer.observe(this.bestSellersContainer, { attributes: true, attributeFilter: ['class'] });
    }
    this.dropdownObserver = observer;
  }

  updateOverlayVisibilityBasedOnDropdown() {
    const resultsVisible = this.resultsContainer && this.resultsContainer.classList.contains('optimized-predictive-search__dropdown--visible');
    const bestVisible = this.bestSellersContainer && this.bestSellersContainer.classList.contains('optimized-predictive-search__dropdown--visible');
    const shouldShow = !!(resultsVisible || bestVisible);

    if (shouldShow && !this.isOverlayVisible) {
      this.lockBodyScroll();
      this.isOverlayVisible = true;
      this.showOverlay();
    } else if (!shouldShow && this.isOverlayVisible) {
      this.unlockBodyScroll();
      this.isOverlayVisible = false;
      this.hideOverlay();
    }
  }

  handleClickOutside(e) {
    if (!this.contains(e.target) && !this.searchInput.contains(e.target)) {
      this.hideResults();
      this.hideBestSellers();
    }
  }

  handleScroll() {
    // Only reposition if dropdown is visible and we're on mobile
    if (window.innerWidth <= 768 && this.closest('.header__top')) {
      const isResultsVisible = this.resultsContainer.classList.contains('optimized-predictive-search__dropdown--visible');
      const isBestSellersVisible = this.bestSellersContainer && this.bestSellersContainer.classList.contains('optimized-predictive-search__dropdown--visible');
      
      if (isResultsVisible || isBestSellersVisible) {
        this.updateMobilePosition();
      }
    }
  }

  handleResize() {
    // No-op: CSS controls positioning; avoid touching inline styles
  }

  updateMobilePosition() {
    // No-op: CSS handles mobile positioning; avoid setting inline styles
  }
}

if (!customElements.get('optimized-predictive-search')) {
  customElements.define('optimized-predictive-search', OptimizedPredictiveSearch);
}

